import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  connectAuthEmulator,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  type Auth
} from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

export const YANDEX_CLIENT_ID = 'bb562d94384f406191ac0ad4c138b67e';
export const YANDEX_REDIRECT_PATH = '/auth/yandex/callback';
export const YANDEX_OAUTH_SCOPES = [
  'login:email',
  'login:info',
  'login:avatar'
];
export const YANDEX_STATE_STORAGE_KEY = 'yandex_oauth_state';
export const YANDEX_RETURN_PATH_KEY = 'yandex_oauth_return_to';

export const getYandexRedirectUri = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return `${window.location.origin}${YANDEX_REDIRECT_PATH}`;
};

export const startYandexOAuth = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!YANDEX_CLIENT_ID) {
    throw new Error('Yandex client ID is not configured');
  }

  const state =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  sessionStorage.setItem(YANDEX_STATE_STORAGE_KEY, state);
  const { pathname, search, hash } = window.location;
  sessionStorage.setItem(
    YANDEX_RETURN_PATH_KEY,
    `${pathname}${search}${hash}`
  );

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: YANDEX_CLIENT_ID,
    redirect_uri: getYandexRedirectUri(),
    scope: YANDEX_OAUTH_SCOPES.join(' '),
    state,
    force_confirm: 'yes'
  });

  window.location.href = `https://oauth.yandex.ru/authorize?${params.toString()}`;
};

// VK ID configuration
export const VK_APP_ID = '54271660';
export const VK_REDIRECT_PATH = '/auth/vk/callback';
export const VK_STATE_STORAGE_KEY = 'vk_oauth_state';
export const VK_RETURN_PATH_KEY = 'vk_oauth_return_to';
export const VK_CODE_VERIFIER_KEY = 'vk_code_verifier';
export const VK_CODE_CHALLENGE_KEY = 'vk_code_challenge';

export const getVkRedirectUri = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return `${window.location.origin}${VK_REDIRECT_PATH}`;
};

const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeChallenge = async (verifier: string) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export const startVkOAuth = async () => {
  if (typeof window === 'undefined') {
    return;
  }

  const state =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  sessionStorage.setItem(VK_STATE_STORAGE_KEY, state);
  sessionStorage.setItem(VK_CODE_VERIFIER_KEY, codeVerifier);
  sessionStorage.setItem(VK_CODE_CHALLENGE_KEY, codeChallenge);
  
  const { pathname, search, hash } = window.location;
  sessionStorage.setItem(
    VK_RETURN_PATH_KEY,
    `${pathname}${search}${hash}`
  );

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: VK_APP_ID,
    redirect_uri: getVkRedirectUri(),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    scope: 'vkid.personal_info',
  });

  window.location.href = `https://id.vk.ru/authorize?${params.toString()}`;
};

// Firebase configuration
// In Vite, use VITE_ prefix for client-side env vars
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || ''
};

// Lazy initialization for SSR compatibility
let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let db: Firestore | undefined;
let functions: Functions | undefined;
let emulatorsConnected = false;

function getApp(): FirebaseApp {
  if (!app) {
    if (getApps().length === 0) {
      app = initializeApp(firebaseConfig);
      
      if (import.meta.env.DEV) {
        console.log('[firebase] Firebase config loaded', {
          apiKeyLoaded: Boolean(firebaseConfig.apiKey),
          authDomain: firebaseConfig.authDomain,
          projectId: firebaseConfig.projectId,
          storageBucket: firebaseConfig.storageBucket,
          messagingSenderId: Boolean(firebaseConfig.messagingSenderId),
          appIdLoaded: Boolean(firebaseConfig.appId)
        });
      }
    } else {
      app = getApps()[0];
    }
  }
  return app;
}

function getAuthInstance(): Auth {
  if (!auth) {
    auth = getAuth(getApp());
  }
  return auth;
}

function getDbInstance(): Firestore {
  if (!db) {
    db = getFirestore(getApp());
  }
  return db;
}

function getFunctionsInstance(): Functions {
  if (!functions) {
    functions = getFunctions(getApp(), 'europe-west1');
    
    // Connect to emulators in dev mode (only once)
    if (import.meta.env.DEV && !emulatorsConnected) {
      try {
        connectFunctionsEmulator(functions, 'localhost', 5002);
        emulatorsConnected = true;
      } catch (error) {
        // Emulator already connected
      }
    }
  }
  return functions;
}

// Export getters for use in components
export { getAuthInstance as getAuth, getDbInstance as getDb, getFunctionsInstance as getFunctions };

// For backwards compatibility - these will be lazily initialized
export const auth_lazy = { get current() { return getAuthInstance(); } };
export const db_lazy = { get current() { return getDbInstance(); } };
export const functions_lazy = { get current() { return getFunctionsInstance(); } };

export default { getApp, getAuth: getAuthInstance, getDb: getDbInstance, getFunctions: getFunctionsInstance };

// Initialize OAuth providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Yandex provider (uses OAuth)
const yandexProvider = new OAuthProvider('oidc.yandex');
yandexProvider.setCustomParameters({
  prompt: 'login'
});
yandexProvider.addScope('openid');
yandexProvider.addScope('login:email');
yandexProvider.addScope('login:info');
yandexProvider.addScope('login:avatar');
yandexProvider.addScope('login:birthday');
yandexProvider.addScope('login:default_phone');

// VK provider (uses OAuth) 
// Note: VK OAuth needs to be configured in Firebase Console
const vkProvider = new OAuthProvider('oidc.vk');

// Helper function to detect if running in Telegram Mini App
// Telegram Mini App has limited sessionStorage access, so we need to use redirect instead of popup
function isTelegramMiniApp(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  // Check for Telegram WebApp SDK
  const telegramWebApp = (window as any).Telegram?.WebApp;
  if (!telegramWebApp) {
    return false;
  }
  
  // Check if we have real Telegram init data (not just empty SDK)
  const hasRealInitData = Boolean(
    telegramWebApp.initData && 
    typeof telegramWebApp.initData === 'string' && 
    telegramWebApp.initData.trim().length > 0
  );
  
  const hasRealAuthDate = Boolean(
    telegramWebApp.initDataUnsafe?.auth_date && 
    typeof telegramWebApp.initDataUnsafe.auth_date === 'number' && 
    telegramWebApp.initDataUnsafe.auth_date > 0
  );
  
  const hasUser = Boolean(
    telegramWebApp.initDataUnsafe?.user && 
    telegramWebApp.initDataUnsafe.user.id
  );
  
  // Also check if sessionStorage is accessible (it might be blocked in Telegram Mini App)
  try {
    const testKey = '__telegram_storage_test__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
  } catch (e) {
    // If sessionStorage is not accessible, we're likely in Telegram Mini App
    return hasRealInitData || hasRealAuthDate || hasUser;
  }
  
  return hasRealInitData || hasRealAuthDate || hasUser;
}

// Auth helper functions
export const authHelpers = {
  // Sign in with email and password
  async signIn(email: string, password: string) {
    const { signInWithEmailAndPassword } = await import('firebase/auth');
    return signInWithEmailAndPassword(getAuthInstance(), email, password);
  },

  // Sign up with email and password
  async signUp(email: string, password: string) {
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    return createUserWithEmailAndPassword(getAuthInstance(), email, password);
  },

  // Sign in with Google
  // Automatically uses redirect in Telegram Mini App (where sessionStorage is limited)
  // Returns UserCredential for popup flow, void for redirect flow
  async signInWithGoogle(useRedirect = false): Promise<any> {
    // In Telegram Mini App, always use redirect because sessionStorage may be inaccessible
    const shouldUseRedirect = useRedirect || isTelegramMiniApp();
    const authInstance = getAuthInstance();
    
    if (shouldUseRedirect) {
      // signInWithRedirect returns Promise<void> - redirect will happen
      // Result will be available via getRedirectResult() after redirect
      await signInWithRedirect(authInstance, googleProvider);
      return undefined; // Explicitly return undefined for redirect flow
    }
    // signInWithPopup returns Promise<UserCredential>
    return signInWithPopup(authInstance, googleProvider);
  },

  // Sign in with Yandex
  async signInWithYandex() {
    startYandexOAuth();
  },

  // Sign in with VK (using custom OAuth flow with VK ID)
  async signInWithVK(useRedirect = false) {
    if (useRedirect) {
      // Fallback to popup if custom flow doesn't work
      return signInWithRedirect(getAuthInstance(), vkProvider);
    }
    // Use custom OAuth flow with VK ID
    await startVkOAuth();
  },

  // Sign in with custom token (for OAuth callbacks)
  async signInWithCustomToken(customToken: string) {
    const { signInWithCustomToken } = await import('firebase/auth');
    return signInWithCustomToken(getAuthInstance(), customToken);
  },

  // Get redirect result (for OAuth redirects)
  async getRedirectResult() {
    return getRedirectResult(getAuthInstance());
  },

  // Send password reset email
  async sendPasswordReset(email: string) {
    const { sendPasswordResetEmail } = await import('firebase/auth');
    return sendPasswordResetEmail(getAuthInstance(), email);
  },

  // Sign out
  async signOut() {
    const { signOut } = await import('firebase/auth');
    return signOut(getAuthInstance());
  },

  // Get current user
  getCurrentUser() {
    return getAuthInstance().currentUser;
  },

  // Update user email
  async updateEmail(newEmail: string) {
    const user = getAuthInstance().currentUser;
    if (!user) throw new Error('No user signed in');
    
    const { updateEmail: firebaseUpdateEmail } = await import('firebase/auth');
    return firebaseUpdateEmail(user, newEmail);
  },

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: any) => void) {
    return firebaseOnAuthStateChanged(getAuthInstance(), callback);
  },

  // Link account with Google
  // Automatically uses redirect in Telegram Mini App (where sessionStorage is limited)
  async linkWithGoogle() {
    const user = getAuthInstance().currentUser;
    if (!user) throw new Error('No user signed in');
    
    // In Telegram Mini App, use redirect because sessionStorage may be inaccessible
    if (isTelegramMiniApp()) {
      const { linkWithRedirect } = await import('firebase/auth');
      return linkWithRedirect(user, googleProvider);
    }
    
    const { linkWithPopup } = await import('firebase/auth');
    return linkWithPopup(user, googleProvider);
  },

  // Link account with Yandex
  async linkWithYandex() {
    const user = getAuthInstance().currentUser;
    if (!user) throw new Error('No user signed in');
    
    const { linkWithPopup } = await import('firebase/auth');
    return linkWithPopup(user, yandexProvider);
  },

  // Link account with VK
  async linkWithVK() {
    const user = getAuthInstance().currentUser;
    if (!user) throw new Error('No user signed in');
    
    const { linkWithPopup } = await import('firebase/auth');
    return linkWithPopup(user, vkProvider);
  },

  // Link account with Email/Password
  async linkWithEmailPassword(email: string, password: string) {
    const user = getAuthInstance().currentUser;
    if (!user) throw new Error('No user signed in');
    
    const { EmailAuthProvider, linkWithCredential } = await import('firebase/auth');
    const credential = EmailAuthProvider.credential(email, password);
    return linkWithCredential(user, credential);
  },

  // Unlink provider from account
  async unlinkProvider(providerId: string) {
    const user = getAuthInstance().currentUser;
    if (!user) throw new Error('No user signed in');
    
    const { unlink } = await import('firebase/auth');
    return unlink(user, providerId);
  },

  // Get linked providers for current user
  getLinkedProviders() {
    const user = getAuthInstance().currentUser;
    if (!user) return [];
    
    return user.providerData.map(provider => ({
      providerId: provider.providerId,
      uid: provider.uid,
      displayName: provider.displayName,
      email: provider.email,
      photoURL: provider.photoURL
    }));
  },

  // Check if provider is linked
  isProviderLinked(providerId: string) {
    const user = getAuthInstance().currentUser;
    if (!user) return false;
    
    return user.providerData.some(provider => provider.providerId === providerId);
  },

  // Parse Firebase auth errors
  parseAuthError(error: any): string {
    const errorCode = error?.code || '';
    const errorMessage = error?.message || '';
    
    // Handle OAuth redirect URI mismatch errors
    if (errorCode === 'auth/popup-blocked' || errorCode === 'auth/popup-closed-by-user') {
      return 'auth.errors.popupClosed';
    }
    
    // Check if error message contains redirect_uri_mismatch
    if (errorMessage.includes('redirect_uri_mismatch') || errorCode === 'auth/redirect-uri-mismatch' || errorMessage.includes('invalid_request') && errorMessage.includes('redirect_uri')) {
      // Get expected redirect URI for Firebase Google OAuth
      const authDomain = firebaseConfig.authDomain || (firebaseConfig.projectId ? `${firebaseConfig.projectId}.firebaseapp.com` : 'not-configured');
      const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      
      let expectedRedirectUri: string;
      if (isLocalhost) {
        // For local development, Firebase Auth uses emulator or needs localhost URI
        const port = window.location.port || '3000';
        expectedRedirectUri = `http://localhost:${port}/__/auth/handler`;
      } else {
        expectedRedirectUri = `https://${authDomain}/__/auth/handler`;
      }
      
      console.error('🔴 Google OAuth redirect_uri_mismatch error');
      console.error('Environment:', isLocalhost ? 'LOCAL DEVELOPMENT' : 'PRODUCTION');
      console.error('Current URL:', typeof window !== 'undefined' ? window.location.href : 'N/A');
      console.error('Expected redirect URI:', expectedRedirectUri);
      console.error('Current authDomain:', firebaseConfig.authDomain || 'not set');
      console.error('Current projectId:', firebaseConfig.projectId || 'not set');
      console.error('');
      console.error('═══════════════════════════════════════════════════════════');
      console.error('ИНСТРУКЦИЯ ПО ИСПРАВЛЕНИЮ (GOOGLE CLOUD CONSOLE):');
      console.error('═══════════════════════════════════════════════════════════');
      console.error('1. Откройте: https://console.cloud.google.com/apis/credentials');
      console.error(`2. Выберите проект: ${firebaseConfig.projectId || 'astrobaby-ai'}`);
      console.error('3. Найдите OAuth 2.0 Client ID (обычно называется "Web client (auto created by Google Service)")');
      console.error('4. Нажмите на него для редактирования');
      console.error('5. В разделе "Authorized redirect URIs" нажмите "ADD URI"');
      console.error(`6. Добавьте ТОЧНО этот URI: ${expectedRedirectUri}`);
      if (!isLocalhost) {
        // Also suggest alternative domains
        const altDomains = [
          `https://${authDomain.replace('.firebaseapp.com', '')}/__/auth/handler`,
          'https://astrobaby.me/__/auth/handler',
          'https://astrobaby-ai.web.app/__/auth/handler',
        ];
        console.error('7. Также добавьте альтернативные домены:');
        altDomains.forEach((uri, idx) => console.error(`   ${idx + 1}. ${uri}`));
        console.error('8. Нажмите "SAVE"');
        console.error('9. Подождите 2-5 минут для распространения изменений');
      } else {
        console.error('7. Для локальной разработки также может понадобиться:');
        console.error('   - http://localhost:9099/__/auth/handler (если используется Auth Emulator)');
        console.error('8. Нажмите "SAVE"');
      }
      console.error('═══════════════════════════════════════════════════════════');
      return 'auth.errors.redirectUriMismatch';
    }
    
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'auth.errors.userNotFound';
      case 'auth/wrong-password':
        return 'auth.errors.wrongPassword';
      case 'auth/email-already-in-use':
        return 'auth.errors.emailInUse';
      case 'auth/weak-password':
        return 'auth.errors.weakPassword';
      case 'auth/invalid-email':
        return 'auth.errors.invalidEmail';
      case 'auth/too-many-requests':
        return 'auth.errors.tooManyRequests';
      case 'auth/network-request-failed':
        return 'auth.errors.networkError';
      case 'auth/popup-closed-by-user':
        return 'auth.errors.popupClosed';
      case 'auth/provider-already-linked':
        return 'auth.errors.providerAlreadyLinked';
      case 'auth/credential-already-in-use':
        return 'auth.errors.credentialInUse';
      default:
        return 'auth.errors.unknownError';
    }
  }
};

